import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useContext,
  useMemo,
} from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  useReactFlow,
  ReactFlowProvider,
  BackgroundVariant,
  Panel,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  useMediaQuery,
  CircularProgress,
  Typography,
  Slide,
  Fab,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { Add as AddIcon } from "@material-ui/icons";

import { toast } from "react-toastify";

import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useNodeStorage } from "../../stores/useNodeStorage";

import { nodeTypes, NODE_CATEGORIES } from "./nodes";
import SmartEdge from "./edges/SmartEdge";
import NodePalette from "./NodePalette";
import FlowToolbar from "./FlowToolbar";
import Inspector from "./Inspector";
import AICopilot from "./AICopilot";
import ExecutionPreview from "./ExecutionPreview";
import VersionHistory from "./VersionHistory";
import useFlowHistory from "./useFlowHistory";
import useAutoSave from "./useAutoSave";
import useAutoLayout from "./useAutoLayout";
import { prepareConnectionsForV2Editor } from "./utils/migrateV1FlowConnections";

// -- Modals V1 (reutilizados para edicao completa) --
import FlowBuilderAddTextModal from "../../components/FlowBuilderAddTextModal";
import FlowBuilderIntervalModal from "../../components/FlowBuilderIntervalModal";
import FlowBuilderConditionModal from "../../components/FlowBuilderConditionModal";
import FlowBuilderMenuModal from "../../components/FlowBuilderMenuModal";
import FlowBuilderAddButtonModal from "../../components/FlowBuilderAddButtonModal";
import FlowBuilderAddButtonPixModal from "../../components/FlowBuilderAddButtonPixModal";
import FlowBuilderAddListModal from "../../components/FlowBuilderAddListModal";
import FlowBuilderAddImgModal from "../../components/FlowBuilderAddImgModal";
import FlowBuilderAddAudioModal from "../../components/FlowBuilderAddAudioModal";
import FlowBuilderAddVideoModal from "../../components/FlowBuilderAddVideoModal";
import FlowBuilderRandomizerModal from "../../components/FlowBuilderRandomizerModal";
import FlowBuilderSingleBlockModal from "../../components/FlowBuilderSingleBlockModal";
import FlowBuilderAddTicketModal from "../../components/FlowBuilderAddTicketModal";
import FlowBuilderAddTagModal from "../../components/FlowBuilderAddTagModal";
import FlowBuilderRemoveTagModal from "../../components/FlowBuilderRemoveTagModal";
import FlowBuilderTagCheckModal from "../../components/FlowBuilderTagCheckModal";
import FlowBuilderAddSwitchFlowModal from "../../components/FlowBuilderAddSwitchFlowModal";
import FlowBuilderAddAttendantModal from "../../components/FlowBuilderAddAttendantModal";
import FlowBuilderAddOpenAIModal from "../../components/FlowBuilderAddOpenAIModal";
import FlowBuilderGeminiModal from "../../components/FlowBuilderGeminiModal";
import FlowBuilderInputModal from "../../components/FlowBuilderInputModal";
import FlowBuilderCampaignProModal from "../../components/FlowBuilderCampaignProModal";
import FlowBuilderKanbanLaneModal from "../../components/FlowBuilderKanbanLaneModal";
import FlowBuilderCloseTicketModal from "../../components/FlowBuilderCloseTicketModal";

const edgeTypes = { buttonedge: SmartEdge };

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
    overflow: "hidden",
    backgroundColor: theme.palette.background.default,
  },
  canvasContainer: {
    flex: 1,
    display: "flex",
    position: "relative",
    overflow: "hidden",
    minHeight: 0, // Garante que flex item nao extrapole o parent
  },
  reactFlowWrapper: {
    flex: 1,
    height: "100%",
    position: "relative",
    zIndex: 1,
  },
  loadingOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.8)",
    zIndex: 50,
  },
  mobileFab: {
    position: "fixed",
    bottom: 24,
    left: 24,
    zIndex: 30,
  },
  mobileBottomSheet: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    maxHeight: "60vh",
    backgroundColor: theme.palette.background.paper,
    borderRadius: "16px 16px 0 0",
    boxShadow: "0 -4px 20px rgba(0,0,0,0.15)",
    zIndex: 40,
    overflowY: "auto",
    padding: "16px",
  },
}));

// Gera ID unico para nodes (mesmo padrao do V1)
function generateId(len = 30) {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < len; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

// ErrorBoundary para capturar erros de renderizacao e mostrar mensagem ao usuario
class FlowErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error("[FlowV2] ❌ Erro de renderizacao:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", height: "100vh", gap: 16, padding: 32,
        }}>
          <Typography variant="h5" style={{ color: "#ef4444" }}>
            Erro no Flow Builder V2
          </Typography>
          <Typography variant="body1" style={{ color: "#666", maxWidth: 500, textAlign: "center" }}>
            Ocorreu um erro ao renderizar o editor. Verifique o console do navegador (F12) para detalhes.
          </Typography>
          <pre style={{
            backgroundColor: "#f5f5f5", padding: 16, borderRadius: 8,
            fontSize: 12, maxWidth: 600, overflow: "auto", border: "1px solid #ddd",
          }}>
            {this.state.error?.message || "Erro desconhecido"}
          </pre>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: "10px 24px", backgroundColor: "#9a00ed", color: "#fff",
              border: "none", borderRadius: 8, cursor: "pointer", fontSize: 14,
            }}
          >
            Recarregar pagina
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const initialNodes = [];
const initialEdges = [];

// ========================================================
// FlowBuilderV2Content (dentro do ReactFlowProvider)
// ========================================================
function FlowBuilderV2Content() {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { id: flowId } = useParams();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { zoomIn, zoomOut, fitView, screenToFlowPosition } = useReactFlow();
  const storageItems = useNodeStorage();

  // ─── Remover padding do layout pai para tela cheia ───
  // Injeta <style> com !important para garantir override do CSS do ModernLayout
  useEffect(() => {
    const style = document.createElement("style");
    style.id = "fb-v2-fullscreen";
    style.textContent = `
      .content-container.fb-v2-active {
        padding: 0 !important;
        overflow: hidden !important;
      }
      .main-content.fb-v2-active {
        overflow-y: hidden !important;
      }
    `;
    document.head.appendChild(style);

    const cc = document.querySelector(".content-container");
    const mc = document.querySelector(".main-content");
    if (cc) cc.classList.add("fb-v2-active");
    if (mc) mc.classList.add("fb-v2-active");

    return () => {
      if (cc) cc.classList.remove("fb-v2-active");
      if (mc) mc.classList.remove("fb-v2-active");
      style.remove();
    };
  }, []);

  // --- State ---
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [loading, setLoading] = useState(true);
  const [flowName, setFlowName] = useState("");
  const [selectedNode, setSelectedNode] = useState(null);
  const [paletteOpen, setPaletteOpen] = useState(!isMobile);
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [simulatorOpen, setSimulatorOpen] = useState(false);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [bottomSheetOpen, setBottomSheetOpen] = useState(false);

  // --- Hooks ---
  const { pushSnapshot, undo, redo, canUndo, canRedo } = useFlowHistory();
  const { triggerSave, forceSave, saveStatus } = useAutoSave(flowId);
  const { getLayoutedElements } = useAutoLayout();

  // --- Modals V1 (para edicao completa de nodes complexos) ---
  // Modais V1 esperam: open="create"|"edit"|null, close(null), onSave(data), onUpdate(data), data={...node, data:{...}}
  const [modalState, setModalState] = useState({ type: null, mode: null, data: null });
  const openModal = useCallback((type, mode, data) => setModalState({ type, mode, data }), []);
  const closeModal = useCallback(() => {
    setModalState({ type: null, mode: null, data: null });
    pendingDropRef.current = null;
  }, []);

  // --- Ref ---
  const reactFlowWrapper = useRef(null);
  const canvasContainerRef = useRef(null);
  const isInitialLoadDone = useRef(false);

  // ─── Prevenir scroll involuntario nos containers ───
  // ReactFlow pode causar scrollLeft/scrollTop no container pai; reseta sempre para 0
  useEffect(() => {
    const resetScroll = (e) => {
      const el = e.currentTarget;
      if (el.scrollLeft !== 0) el.scrollLeft = 0;
      if (el.scrollTop !== 0) el.scrollTop = 0;
    };
    const el = canvasContainerRef.current;
    if (el) el.addEventListener("scroll", resetScroll);
    // Tambem prevenir no root (pai do toolbar + canvas)
    const root = el?.parentElement;
    if (root) root.addEventListener("scroll", resetScroll);
    return () => {
      if (el) el.removeEventListener("scroll", resetScroll);
      if (root) root.removeEventListener("scroll", resetScroll);
    };
  }, []);

  // ─── Carregar dados do fluxo ───
  useEffect(() => {
    if (!flowId) {
      setLoading(false);
      isInitialLoadDone.current = true;
      return;
    }
    isInitialLoadDone.current = false;
    (async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/flowbuilder/flow/${flowId}`);
        console.log("[FlowV2] Raw API response:", JSON.stringify(data).substring(0, 500));

        // Trata dados do fluxo - pode ser string (MySQL) ou objeto
        let flowData = data?.flow?.flow;
        if (typeof flowData === "string") {
          try { flowData = JSON.parse(flowData); } catch (e) { console.error("[FlowV2] Failed to parse flow JSON string"); }
        }

        console.log("[FlowV2] Parsed flow:", { hasFlow: !!flowData, nodeCount: flowData?.nodes?.length, edgeCount: flowData?.connections?.length });

        if (flowData && flowData.nodes && flowData.nodes.length > 0) {
          const loadedNodes = flowData.nodes;

          const loadedEdges = prepareConnectionsForV2Editor(
            loadedNodes,
            flowData.connections || []
          );
          // Log dos tipos de node para debug
          const typeCounts = {};
          loadedNodes.forEach((n) => { typeCounts[n.type] = (typeCounts[n.type] || 0) + 1; });
          console.log("[FlowV2] Node types:", typeCounts);

          // Remove propriedades V1 que conflitam com a renderizacao V2
          loadedNodes.forEach((n) => {
            delete n.width;
            delete n.height;
            delete n.measured;
            delete n.style;           // Estilos inline V1 conflitam com componentes V2
            delete n.positionAbsolute; // Artefato do V1, nao usado no V2
            delete n.dragging;         // Estado de arraste do V1
            delete n.selected;         // Reseta selecao ao carregar
          });

          setNodes(loadedNodes);
          setEdges(loadedEdges);
          pushSnapshot(loadedNodes, loadedEdges);
          console.log("[FlowV2] ✅ Loaded", loadedNodes.length, "nodes and", loadedEdges.length, "edges");
          // Ajusta a visualizacao apos nodes serem medidos (da tempo pro React renderizar)
          setTimeout(() => {
            try { fitView({ padding: 0.2, duration: 300 }); } catch (e) {}
          }, 300);
        } else {
          console.warn("[FlowV2] ⚠️ No flow data found in API response. Full data:", data);
        }
        setFlowName(data?.flow?.name || "");
      } catch (err) {
        console.error("[FlowV2] Error loading flow:", err);
        toast.error("Erro ao carregar fluxo");
      } finally {
        setLoading(false);
        // Marca carregamento inicial como concluido APOS atualizacoes de estado
        setTimeout(() => {
          isInitialLoadDone.current = true;
        }, 100);
      }
    })();
  }, [flowId]); // eslint-disable-line

  // ─── Acoes do Zustand store (deletar/duplicar pelo menu do node) ───
  useEffect(() => {
    if (storageItems.action === "delete" && storageItems.node) {
      const nodeId = storageItems.node;
      setNodes((nds) => nds.filter((n) => n.id !== nodeId));
      setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
      storageItems.setAct("idle");
      if (selectedNode?.id === nodeId) setSelectedNode(null);
    }
    if (storageItems.action === "duplicate" && storageItems.node) {
      const nodeId = storageItems.node;
      setNodes((nds) => {
        const original = nds.find((n) => n.id === nodeId);
        if (!original) return nds;
        return [
          ...nds,
          {
            ...JSON.parse(JSON.stringify(original)),
            id: generateId(),
            position: {
              x: original.position.x + (original.width || 200) + 40,
              y: original.position.y,
            },
            selected: false,
          },
        ];
      });
      storageItems.setAct("idle");
    }
    if (storageItems.action === "deleteEdge" && storageItems.node) {
      const edgeId = storageItems.node;
      setEdges((eds) => eds.filter((e) => e.id !== edgeId));
      storageItems.setAct("idle");
    }
  }, [storageItems.action, storageItems.node]); // eslint-disable-line

  // ─── Auto-save nas mudancas (SO apos carregamento inicial completo) ───
  useEffect(() => {
    if (!loading && flowId && isInitialLoadDone.current) {
      triggerSave(nodes, edges);
    }
  }, [nodes, edges]); // eslint-disable-line

  // ─── Salvar historico nas mudancas (so apos carregamento inicial) ───
  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);
  useEffect(() => {
    if (!isInitialLoadDone.current) return;
    if (JSON.stringify(nodes) !== JSON.stringify(nodesRef.current) ||
        JSON.stringify(edges) !== JSON.stringify(edgesRef.current)) {
      pushSnapshot(nodes, edges);
      nodesRef.current = nodes;
      edgesRef.current = edges;
    }
  }, [nodes, edges]); // eslint-disable-line

  // ─── Atalhos de teclado ───
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignora quando esta digitando em inputs
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.isContentEditable) return;

      // Ctrl+Z = desfazer
      if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        const prev = undo();
        if (prev) { setNodes(prev.nodes); setEdges(prev.edges); }
      }
      // Ctrl+Shift+Z = refazer
      if ((e.ctrlKey || e.metaKey) && e.key === "z" && e.shiftKey) {
        e.preventDefault();
        const next = redo();
        if (next) { setNodes(next.nodes); setEdges(next.edges); }
      }
      // Ctrl+S = salvar
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        forceSave(nodes, edges);
      }
      // Ctrl+D = duplicar
      if ((e.ctrlKey || e.metaKey) && e.key === "d") {
        e.preventDefault();
        if (selectedNode) {
          storageItems.setNodesStorage(selectedNode.id);
          storageItems.setAct("duplicate");
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedNode, nodes, edges]); // eslint-disable-line

  // ─── Validacao de conexao ───
  const isValidConnection = useCallback(
    (connection) => {
      // Sem auto-conexao (loop no proprio node)
      if (connection.source === connection.target) return false;

      // Sem edges duplicados (mesmo source+sourceHandle → mesmo target+targetHandle)
      const duplicate = edges.some(
        (e) =>
          e.source === connection.source &&
          e.target === connection.target &&
          (e.sourceHandle || null) === (connection.sourceHandle || null) &&
          (e.targetHandle || null) === (connection.targetHandle || null)
      );
      if (duplicate) return false;

      // Node destino deve existir e nao pode ser node de inicio
      const targetNode = nodes.find((n) => n.id === connection.target);
      if (targetNode?.type === "start") return false;

      return true;
    },
    [edges, nodes]
  );

  // ─── Handler de conexao ───
  const onConnect = useCallback(
    (params) => {
      setEdges((eds) => addEdge({ ...params, type: "buttonedge" }, eds));
    },
    [setEdges]
  );

  // ─── Clique no node → abre Inspector ───
  const onNodeClick = useCallback((_, node) => {
    setSelectedNode(node);
  }, []);

  // ─── Clique no canvas → fecha Inspector ───
  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const pendingDropRef = useRef(null);

  // ─── Posicao para novo node (direita do ultimo) ───
  const getNextNodePosition = useCallback(() => {
    if (nodes.length === 0) return { x: 250, y: 100 };
    const last = nodes[nodes.length - 1];
    return {
      x: last.position.x + (last.width || 200) + 40,
      y: last.position.y,
    };
  }, [nodes]);

  // ─── Adicionar node em posicao especifica ───
  const addNodeAtPosition = useCallback(
    (type, data = {}, position = null) => {
      const pos = position || getNextNodePosition();
      const newNode = {
        id: generateId(),
        position: pos,
        data: { label: "", ...data },
        type,
      };
      // Groups precisam de dimensoes iniciais e zIndex baixo
      if (type === "group") {
        newNode.data.label = data.label || "Novo Grupo";
        newNode.data.groupColor = data.groupColor || "blue";
        newNode.style = { width: 400, height: 300, zIndex: -1 };
        newNode.zIndex = -1;
      }
      setNodes((nds) => [...nds, newNode]);
    },
    [setNodes, getNextNodePosition]
  );

  // ─── Abrir modal V1 para edicao completa do node ───
  // Modais V1 esperam dados no formato: { id, type, data: { campo1, campo2, ... } }
  const openLegacyModal = useCallback((type, node, dropPosition) => {
    const mode = node ? "edit" : "create";
    console.log("[FlowV2] openLegacyModal:", { type, mode, nodeId: node?.id, hasData: !!node?.data });
    if (node) {
      // Edicao: passa node no formato V1 { id, type, data: {...} }
      const v1Data = { id: node.id, type: node.type, data: { ...(node.data || {}) }, _dropPosition: dropPosition };
      openModal(type, mode, v1Data);
    } else {
      // Criacao: sem dados, apenas guarda posicao do drop
      openModal(type, mode, { _dropPosition: dropPosition });
    }
  }, [openModal]);

  // ─── Duplo-clique → abre modal ───
  const onNodeDoubleClick = useCallback((_, node) => {
    openLegacyModal(node.type, node);
  }, [openLegacyModal]);

  // ─── Arrastar e soltar da paleta ───
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      const type = event.dataTransfer.getData("application/reactflow");
      if (!type) return;

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      // Nodes que precisam de modal para configuracao inicial
      const needsModal = [
        "message", "menu", "button", "buttonPix", "list", "condition",
        "singleBlock", "ticket", "tag", "removeTag", "tagCheck", "switchFlow",
        "attendant", "openai", "gemini", "input", "campaignPro", "kanbanLane", "closeTicket",
        "randomizer", "interval", "img", "audio", "video",
      ];

      if (needsModal.includes(type)) {
        // Guarda posicao para usar depois que o modal fechar
        pendingDropRef.current = { type, position };
        openLegacyModal(type, null, position);
        return;
      }

      // Nodes simples: adiciona direto
      addNodeAtPosition(type, {}, position);
    },
    [screenToFlowPosition, openLegacyModal, addNodeAtPosition]
  );

  // ─── Salvar modal (CRIAR novo node) ───
  const handleModalSave = useCallback(
    (type, data) => {
      const dropPos = modalState.data?._dropPosition || pendingDropRef.current?.position;
      const position = dropPos || getNextNodePosition();
      const newNode = {
        id: generateId(),
        position,
        data: { ...data },
        type,
      };
      setNodes((nds) => [...nds, newNode]);
      pendingDropRef.current = null;
      closeModal();
    },
    [modalState, setNodes, getNextNodePosition, closeModal]
  );

  // ─── Atualizar modal (EDITAR node existente) ───
  // Modais V1 chamam onUpdate com o objeto completo: { ...nodeOriginal, data: { ...camposAtualizados } }
  const handleModalUpdate = useCallback(
    (type, updatedNode) => {
      const nodeId = updatedNode?.id || modalState.data?.id;
      if (!nodeId) return;
      const newData = updatedNode?.data || {};
      setNodes((nds) =>
        nds.map((n) =>
          n.id === nodeId ? { ...n, data: { ...n.data, ...newData } } : n
        )
      );
      // Atualiza Inspector se este node esta selecionado
      setSelectedNode((prev) =>
        prev?.id === nodeId ? { ...prev, data: { ...prev.data, ...newData } } : prev
      );
      closeModal();
    },
    [modalState, setNodes, closeModal]
  );

  // ─── Atualizar node pelo Inspector ───
  const handleUpdateNode = useCallback(
    (nodeId, newData) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === nodeId ? { ...n, data: { ...n.data, ...newData } } : n
        )
      );
      // Atualiza referencia do node selecionado
      setSelectedNode((prev) =>
        prev?.id === nodeId ? { ...prev, data: { ...prev.data, ...newData } } : prev
      );
    },
    [setNodes]
  );

  // ─── Alterar nome do fluxo ───
  const handleNameChange = useCallback(
    async (name) => {
      setFlowName(name);
      try {
        await api.put("/flowbuilder", { name, flowBuilderId: flowId });
      } catch (err) {
        console.error("[FlowV2] Error updating name:", err);
      }
    },
    [flowId]
  );

  // ─── Adicionar node por clique na paleta (fallback) ───
  const handlePaletteAddNode = useCallback(
    (type) => {
      const needsModal = [
        "message", "menu", "button", "buttonPix", "list", "condition",
        "singleBlock", "ticket", "tag", "removeTag", "tagCheck", "switchFlow",
        "attendant", "openai", "gemini", "input", "campaignPro", "kanbanLane", "closeTicket",
        "randomizer", "interval", "img", "audio", "video",
      ];
      if (needsModal.includes(type)) {
        openLegacyModal(type, null);
      } else {
        addNodeAtPosition(type, {});
      }
      if (isMobile) setBottomSheetOpen(false);
    },
    [openLegacyModal, addNodeAtPosition, isMobile]
  );

  // ─── Layout automatico ───
  const handleAutoLayout = useCallback(() => {
    const layouted = getLayoutedElements(nodes, edges, "LR");
    setNodes(layouted);
    pushSnapshot(layouted, edges);
    // Ajusta visualizacao apos layout com delay para renderizacao
    setTimeout(() => fitView({ padding: 0.2, duration: 400 }), 50);
  }, [nodes, edges, getLayoutedElements, setNodes, pushSnapshot, fitView]);

  // ─── AI Copilot: aplicar fluxo gerado ───
  const handleApplyAIFlow = useCallback((flowData) => {
    if (!flowData?.nodes || !flowData?.connections) return;

    // Converte nodes gerados pela IA para formato React Flow
    const newNodes = flowData.nodes.map((n) => ({
      id: String(n.id),
      type: n.type,
      position: n.position || { x: 250, y: 100 },
      data: n.data || {},
    }));

    const normalizedConnections = prepareConnectionsForV2Editor(
      newNodes,
      flowData.connections
    );
    const newEdges = normalizedConnections.map((c, idx) => ({
      id: c.id || `ai_e${idx}`,
      source: String(c.source),
      sourceHandle: c.sourceHandle || "a",
      target: String(c.target),
      targetHandle: c.targetHandle || null,
      type: c.type || "buttonedge",
    }));

    setNodes(newNodes);
    setEdges(newEdges);
    pushSnapshot(newNodes, newEdges);
    toast.success(`Fluxo IA aplicado: ${newNodes.length} nodes`);
    setTimeout(() => fitView({ padding: 0.2, duration: 400 }), 100);
  }, [setNodes, setEdges, pushSnapshot, fitView]);

  // ─── Destacar node no canvas (scroll + flash) ───
  const handleHighlightNode = useCallback((nodeId) => {
    const targetNode = nodes.find((n) => n.id === nodeId);
    if (!targetNode) return;

    // 1. Centraliza o canvas no node
    fitView({
      nodes: [targetNode],
      padding: 0.5,
      duration: 500,
      maxZoom: 1.5,
    });

    // 2. Seleciona o node
    setSelectedNode(targetNode);

    // 3. Flash visual: adiciona/remove classe CSS temporaria
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id === nodeId) {
          return { ...n, data: { ...n.data, _highlight: true } };
        }
        return { ...n, data: { ...n.data, _highlight: false } };
      })
    );
    // Remove highlight apos 2s
    setTimeout(() => {
      setNodes((nds) =>
        nds.map((n) => ({ ...n, data: { ...n.data, _highlight: false } }))
      );
    }, 2000);
  }, [nodes, fitView, setNodes, setSelectedNode]);

  // ─── Estilo da linha de conexao ───
  const connectionLineStyle = useMemo(
    () => ({
      stroke: "#94a3b8",
      strokeWidth: 1.5,
    }),
    []
  );

  // ─── Renderizacao ───
  return (
    <div className={classes.root}>
      {/* Barra de ferramentas */}
      <FlowToolbar
        flowName={flowName}
        onNameChange={handleNameChange}
        saveStatus={saveStatus}
        onSave={() => forceSave(nodes, edges)}
        onUndo={() => {
          const prev = undo();
          if (prev) { setNodes(prev.nodes); setEdges(prev.edges); }
        }}
        onRedo={() => {
          const next = redo();
          if (next) { setNodes(next.nodes); setEdges(next.edges); }
        }}
        canUndo={canUndo}
        canRedo={canRedo}
        onZoomIn={() => zoomIn()}
        onZoomOut={() => zoomOut()}
        onFitView={() => fitView({ padding: 0.2 })}
        onAutoLayout={handleAutoLayout}
        onToggleCopilot={() => setCopilotOpen((p) => !p)}
        onToggleSimulator={() => setSimulatorOpen((p) => !p)}
        onToggleVersionHistory={() => setVersionHistoryOpen((p) => !p)}
        reactFlowWrapper={reactFlowWrapper}
      />

      <div className={classes.canvasContainer} ref={canvasContainerRef}>
        {/* Paleta de nodes (Desktop) */}
        {!isMobile && (
          <NodePalette
            collapsed={!paletteOpen}
            onToggle={() => setPaletteOpen((p) => !p)}
            onAddNode={handlePaletteAddNode}
          />
        )}

        {/* Canvas principal */}
        <div className={classes.reactFlowWrapper} ref={reactFlowWrapper}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            isValidConnection={isValidConnection}
            onNodeClick={onNodeClick}
            onNodeDoubleClick={onNodeDoubleClick}
            onPaneClick={onPaneClick}
            onDrop={onDrop}
            onDragOver={onDragOver}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            connectionLineStyle={connectionLineStyle}
            deleteKeyCode={["Backspace", "Delete"]}
            snapToGrid
            snapGrid={[20, 20]}
            minZoom={0.1}
            maxZoom={2}
            defaultEdgeOptions={{ type: "buttonedge" }}
          >
            <Background variant={BackgroundVariant.Dots} gap={20} size={1.5} color="#d0d5dd" />
            <MiniMap
              nodeStrokeWidth={3}
              zoomable
              pannable
              style={{ borderRadius: 8, border: "1px solid #e0e0e0" }}
            />
            {!isMobile && <Controls showInteractive={false} />}
          </ReactFlow>
        </div>

        {/* Inspector lateral (Desktop) */}
        <Inspector
          node={selectedNode}
          onClose={() => setSelectedNode(null)}
          onUpdateNode={handleUpdateNode}
          onOpenLegacyModal={(type, node) => openLegacyModal(type, node)}
        />

        {/* AI Copilot */}
        <AICopilot
          open={copilotOpen}
          onClose={() => setCopilotOpen(false)}
          nodes={nodes}
          edges={edges}
          onApplyFlow={handleApplyAIFlow}
          onHighlightNode={handleHighlightNode}
        />

        {/* Simulador de execucao */}
        <ExecutionPreview
          open={simulatorOpen}
          onClose={() => setSimulatorOpen(false)}
          nodes={nodes}
          edges={edges}
          setNodes={setNodes}
          fitView={fitView}
        />

        {/* Historico de versoes */}
        <VersionHistory
          open={versionHistoryOpen}
          onClose={() => setVersionHistoryOpen(false)}
          flowId={flowId}
          onRestore={(restoredData) => {
            const restoredNodes = (restoredData.nodes || []).map((n) => ({
              ...n,
              position: n.position || { x: 0, y: 0 },
              data: n.data || {},
              type: n.type || "default",
            }));
            const restoredEdges = prepareConnectionsForV2Editor(
              restoredNodes,
              restoredData.connections || []
            );
            setNodes(restoredNodes);
            setEdges(restoredEdges);
            fitView({ padding: 0.2 });
          }}
        />

        {/* Overlay de carregamento */}
        {loading && (
          <div className={classes.loadingOverlay}>
            <CircularProgress />
            <Typography style={{ marginLeft: 12 }}>Carregando fluxo...</Typography>
          </div>
        )}
      </div>

      {/* Botao flutuante mobile */}
      {isMobile && (
        <Fab
          color="primary"
          className={classes.mobileFab}
          onClick={() => setBottomSheetOpen(true)}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Painel inferior mobile */}
      <Slide direction="up" in={bottomSheetOpen && isMobile} mountOnEnter unmountOnExit>
        <div className={classes.mobileBottomSheet}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <Typography variant="h6">Adicionar node</Typography>
            <IconButton size="small" onClick={() => setBottomSheetOpen(false)}>
              <span style={{ fontSize: 20 }}>&times;</span>
            </IconButton>
          </div>
          <NodePalette
            collapsed={false}
            onToggle={() => {}}
            onAddNode={handlePaletteAddNode}
          />
        </div>
      </Slide>

      {/* ── Modais V1 (reutilizados) ──
           API V1: open="create"|"edit", close(null), onSave(data), onUpdate(data), data={id,type,data:{...}}
      */}
      {modalState.type === "message" && (
        <FlowBuilderAddTextModal
          open={modalState.mode}
          close={closeModal}
          data={modalState.mode === "edit" ? modalState.data : undefined}
          onSave={(data) => handleModalSave("message", { label: data.text })}
          onUpdate={(updatedNode) => handleModalUpdate("message", updatedNode)}
        />
      )}
      {modalState.type === "interval" && (
        <FlowBuilderIntervalModal
          open={modalState.mode}
          close={closeModal}
          data={modalState.mode === "edit" ? modalState.data : undefined}
          onSave={(data) => handleModalSave("interval", { label: `Intervalo ${data.sec} seg.`, sec: data.sec })}
          onUpdate={(updatedNode) => handleModalUpdate("interval", updatedNode)}
        />
      )}
      {modalState.type === "condition" && (
        <FlowBuilderConditionModal
          open={modalState.mode}
          close={closeModal}
          data={modalState.mode === "edit" ? modalState.data : undefined}
          onSave={(data) => handleModalSave("condition", { key: data.key, condition: data.condition, value: data.value })}
          onUpdate={(updatedNode) => handleModalUpdate("condition", updatedNode)}
        />
      )}
      {modalState.type === "menu" && (
        <FlowBuilderMenuModal
          open={modalState.mode}
          close={closeModal}
          data={modalState.mode === "edit" ? modalState.data : undefined}
          onSave={(data) => handleModalSave("menu", { message: data.message, arrayOption: data.arrayOption, typeMenu: data.typeMenu })}
          onUpdate={(updatedNode) => handleModalUpdate("menu", updatedNode)}
        />
      )}
      {modalState.type === "button" && (
        <FlowBuilderAddButtonModal
          open={modalState.mode}
          close={closeModal}
          data={modalState.mode === "edit" ? modalState.data : undefined}
          onSave={(data) => handleModalSave("button", { message: data.message, arrayOption: data.arrayOption })}
          onUpdate={(updatedNode) => handleModalUpdate("button", updatedNode)}
        />
      )}
      {modalState.type === "buttonPix" && (
        <FlowBuilderAddButtonPixModal
          open={modalState.mode}
          close={closeModal}
          data={modalState.mode === "edit" ? modalState.data : undefined}
          onSave={(data) => handleModalSave("buttonPix", data)}
          onUpdate={(updatedNode) => handleModalUpdate("buttonPix", updatedNode)}
        />
      )}
      {modalState.type === "list" && (
        <FlowBuilderAddListModal
          open={modalState.mode}
          close={closeModal}
          data={modalState.mode === "edit" ? modalState.data : undefined}
          onSave={(data) => handleModalSave("list", data)}
          onUpdate={(updatedNode) => handleModalUpdate("list", updatedNode)}
        />
      )}
      {modalState.type === "img" && (
        <FlowBuilderAddImgModal
          open={modalState.mode}
          close={closeModal}
          data={modalState.mode === "edit" ? modalState.data : undefined}
          onSave={(data) => handleModalSave("img", data)}
          onUpdate={(updatedNode) => handleModalUpdate("img", updatedNode)}
        />
      )}
      {modalState.type === "audio" && (
        <FlowBuilderAddAudioModal
          open={modalState.mode}
          close={closeModal}
          data={modalState.mode === "edit" ? modalState.data : undefined}
          onSave={(data) => handleModalSave("audio", data)}
          onUpdate={(updatedNode) => handleModalUpdate("audio", updatedNode)}
        />
      )}
      {modalState.type === "video" && (
        <FlowBuilderAddVideoModal
          open={modalState.mode}
          close={closeModal}
          data={modalState.mode === "edit" ? modalState.data : undefined}
          onSave={(data) => handleModalSave("video", data)}
          onUpdate={(updatedNode) => handleModalUpdate("video", updatedNode)}
        />
      )}
      {modalState.type === "randomizer" && (
        <FlowBuilderRandomizerModal
          open={modalState.mode}
          close={closeModal}
          data={modalState.mode === "edit" ? modalState.data : undefined}
          onSave={(data) => handleModalSave("randomizer", data)}
          onUpdate={(updatedNode) => handleModalUpdate("randomizer", updatedNode)}
        />
      )}
      {modalState.type === "singleBlock" && (
        <FlowBuilderSingleBlockModal
          open={modalState.mode}
          close={closeModal}
          data={modalState.mode === "edit" ? modalState.data : undefined}
          onSave={(data) => handleModalSave("singleBlock", data)}
          onUpdate={(updatedNode) => handleModalUpdate("singleBlock", updatedNode)}
        />
      )}
      {modalState.type === "ticket" && (
        <FlowBuilderAddTicketModal
          open={modalState.mode}
          close={closeModal}
          data={modalState.mode === "edit" ? modalState.data : undefined}
          onSave={(data) => handleModalSave("ticket", data)}
          onUpdate={(updatedNode) => handleModalUpdate("ticket", updatedNode)}
        />
      )}
      {modalState.type === "tag" && (
        <FlowBuilderAddTagModal
          open={modalState.mode}
          close={closeModal}
          data={modalState.mode === "edit" ? modalState.data : undefined}
          onSave={(data) => handleModalSave("tag", data)}
          onUpdate={(updatedNode) => handleModalUpdate("tag", updatedNode)}
        />
      )}
      {modalState.type === "removeTag" && (
        <FlowBuilderRemoveTagModal
          open={modalState.mode}
          close={closeModal}
          data={modalState.mode === "edit" ? modalState.data : undefined}
          onSave={(data) => handleModalSave("removeTag", data)}
          onUpdate={(updatedNode) => handleModalUpdate("removeTag", updatedNode)}
        />
      )}
      {modalState.type === "tagCheck" && (
        <FlowBuilderTagCheckModal
          open={modalState.mode}
          close={closeModal}
          data={modalState.mode === "edit" ? modalState.data : undefined}
          onSave={(data) => handleModalSave("tagCheck", data)}
          onUpdate={(updatedNode) => handleModalUpdate("tagCheck", updatedNode)}
        />
      )}
      {modalState.type === "switchFlow" && (
        <FlowBuilderAddSwitchFlowModal
          open={modalState.mode}
          close={closeModal}
          data={modalState.mode === "edit" ? modalState.data : undefined}
          onSave={(data) => handleModalSave("switchFlow", data)}
          onUpdate={(updatedNode) => handleModalUpdate("switchFlow", updatedNode)}
        />
      )}
      {modalState.type === "attendant" && (
        <FlowBuilderAddAttendantModal
          open={modalState.mode}
          close={closeModal}
          data={modalState.mode === "edit" ? modalState.data : undefined}
          onSave={(data) => handleModalSave("attendant", data)}
          onUpdate={(updatedNode) => handleModalUpdate("attendant", updatedNode)}
        />
      )}
      {modalState.type === "openai" && (
        <FlowBuilderAddOpenAIModal
          open={modalState.mode}
          close={closeModal}
          data={modalState.mode === "edit" ? modalState.data : undefined}
          onSave={(data) => handleModalSave("openai", data)}
          onUpdate={(updatedNode) => handleModalUpdate("openai", updatedNode)}
        />
      )}
      {modalState.type === "gemini" && (
        <FlowBuilderGeminiModal
          open={modalState.mode}
          close={closeModal}
          data={modalState.mode === "edit" ? modalState.data : undefined}
          onSave={(data) => handleModalSave("gemini", data)}
          onUpdate={(updatedNode) => handleModalUpdate("gemini", updatedNode)}
        />
      )}
      {modalState.type === "input" && (
        <FlowBuilderInputModal
          open={modalState.mode}
          close={closeModal}
          data={modalState.mode === "edit" ? modalState.data : undefined}
          onSave={(data) => handleModalSave("input", data)}
          onUpdate={(updatedNode) => handleModalUpdate("input", updatedNode)}
        />
      )}
      {modalState.type === "kanbanLane" && (
        <FlowBuilderKanbanLaneModal
          open={modalState.mode}
          close={closeModal}
          data={modalState.mode === "edit" ? modalState.data : undefined}
          onSave={(data) => handleModalSave("kanbanLane", data)}
          onUpdate={(updatedNode) => handleModalUpdate("kanbanLane", updatedNode)}
        />
      )}
      {modalState.type === "closeTicket" && (
        <FlowBuilderCloseTicketModal
          open={modalState.mode}
          close={closeModal}
          data={modalState.mode === "edit" ? modalState.data : undefined}
          onSave={(data) => handleModalSave("closeTicket", data)}
          onUpdate={(updatedNode) => handleModalUpdate("closeTicket", updatedNode)}
        />
      )}
      {modalState.type === "campaignPro" && (
        <FlowBuilderCampaignProModal
          open={modalState.mode}
          close={closeModal}
          data={modalState.mode === "edit" ? modalState.data : undefined}
          onSave={(data) => handleModalSave("campaignPro", data)}
          onUpdate={(updatedNode) => handleModalUpdate("campaignPro", updatedNode)}
        />
      )}
    </div>
  );
}

// ========================================================
// Componente principal com ReactFlowProvider
// ========================================================
export default function FlowBuilderV2() {
  return (
    <FlowErrorBoundary>
      <ReactFlowProvider>
        <FlowBuilderV2Content />
      </ReactFlowProvider>
    </FlowErrorBoundary>
  );
}
